import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Table, Spinner, Form, Card, Row, Col } from "react-bootstrap";
import Layout from "../../layout/Layout";
import { sureAnaliz } from "../../../api/uretim/makineDataApi";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);
const aylar = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
];
function BoruSureAnaliz() {
  const now = new Date();

  // --- STATE TANIMLARI ---
  const [year, setYear] = useState(String(now.getFullYear()));
  const [monthName, setMonthName] = useState("");
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);

  // DEĞİŞİKLİK 1: Artık array değil, tek bir string veya null tutuyoruz.
  const [expandedDate, setExpandedDate] = useState(null);

  useEffect(() => {
    setMonthName(aylar[now.getMonth()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = useCallback(async () => {
    const monthIndex = aylar.indexOf(monthName) + 1;
    if (!year || !monthName) return;

    try {
      setLoading(true);
      const res = await sureAnaliz(year, monthIndex);
      if (res.data && res.data.success) {
        setApiData(res.data);
      } else {
        setApiData(null);
      }
    } catch (err) {
      console.error("Veri çekme hatası:", err);
      setApiData(null);
    } finally {
      setLoading(false);
    }
  }, [year, monthName]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // DEĞİŞİKLİK 2: Tek satır açma mantığı
  const toggleRow = (date) => {
    if (expandedDate === date) {
      setExpandedDate(null); // Zaten açıksa kapat
    } else {
      setExpandedDate(date); // Değilse aç (Diğerleri otomatik kapanır çünkü state tek değer tutuyor)
    }
  };

  const { tableRows, machineKeys, chartData, grandTotals } = useMemo(() => {
    if (!apiData || !apiData.makinaGunBazliSure) {
      return {
        tableRows: [],
        machineKeys: [],
        chartData: null,
        grandTotals: { durus: 0, fiili: 0, toplam: 0 },
      };
    }

    const { makinaGunBazliSure, makinaAylikToplamSure } = apiData;
    const keys = Object.keys(makinaGunBazliSure).sort();

    let allDates = new Set();
    keys.forEach((k) => {
      if (makinaGunBazliSure[k]) {
        Object.keys(makinaGunBazliSure[k]).forEach((d) => allDates.add(d));
      }
    });

    // Tarih sıralaması (01 -> 31)
    const sortedDates = Array.from(allDates).sort();

    const rows = sortedDates.map((date) => {
      const dObj = new Date(date);
      const dayStr = `${dObj.getDate()} ${monthName.slice(0, 3)}`;

      const rowData = {
        dateDisplay: dayStr,
        fullDate: date,
        machines: {},
        dailyTotal: { durus: 0, fiili: 0, toplam: 0 },
      };

      keys.forEach((key) => {
        const data = makinaGunBazliSure[key]?.[date] || {
          durus: 0,
          fiili: 0,
          toplam: 0,
        };
        rowData.machines[key] = data;

        rowData.dailyTotal.durus += data.durus || 0;
        rowData.dailyTotal.fiili += data.fiili || 0;
        rowData.dailyTotal.toplam += data.toplam || 0;
      });

      return rowData;
    });

    const cData = {
      labels: keys.map((k) => `Makine ${k}`),
      datasets: [
        {
          label: "Fiili Çalışma (dk)",
          data: keys.map((k) => makinaAylikToplamSure[k]?.fiili || 0),
          backgroundColor: "rgba(75, 192, 192, 0.7)",
        },
        {
          label: "Duruş (dk)",
          data: keys.map((k) => makinaAylikToplamSure[k]?.durus || 0),
          backgroundColor: "rgba(255, 99, 132, 0.7)",
        },
      ],
    };

    let factoryTotal = { durus: 0, fiili: 0, toplam: 0 };
    if (makinaAylikToplamSure) {
      keys.forEach((k) => {
        factoryTotal.durus += makinaAylikToplamSure[k]?.durus || 0;
        factoryTotal.fiili += makinaAylikToplamSure[k]?.fiili || 0;
        factoryTotal.toplam += makinaAylikToplamSure[k]?.toplam || 0;
      });
    }

    return {
      tableRows: rows,
      machineKeys: keys,
      chartData: cData,
      grandTotals: factoryTotal,
    };
  }, [apiData, monthName]);

  const formatNum = (num) => {
    if (!num && num !== 0) return "0";
    return Number(num).toLocaleString("tr-TR");
  };

  return (
    <Layout>
      <div className="container-fluid mt-3">
        {/* ÜST KISIM */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="fw-bold mb-0">Makine Süre Analizi</h5>
            <small className="text-muted" style={{ fontSize: "11px" }}>
              Detay için satıra tıklayın.
            </small>
          </div>
          <div className="d-flex gap-2">
            <Form.Select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              size="sm"
              style={{ width: "90px", fontSize: "12px" }}
            >
              <option value="2026">2026</option>
              <option value="2025">2025</option>
            </Form.Select>
            <Form.Select
              value={monthName}
              onChange={(e) => setMonthName(e.target.value)}
              size="sm"
              style={{ width: "110px", fontSize: "12px" }}
            >
              {aylar.map((ay) => (
                <option key={ay} value={ay}>
                  {ay}
                </option>
              ))}
            </Form.Select>
          </div>
        </div>

        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {!loading && apiData && (
          <>
            <div
              className="table-responsive shadow-sm"
              style={{ maxHeight: "75vh", overflowY: "auto" }}
            >
              <Table
                bordered
                hover
                size="sm"
                className="text-center align-middle mb-0"
                style={{ fontSize: "12px", borderColor: "#dee2e6" }}
              >
                <thead
                  className="table-light sticky-top"
                  style={{ top: 0, zIndex: 5 }}
                >
                  <tr>
                    <th
                      className="align-middle bg-white"
                      style={{
                        minWidth: "80px",
                        borderBottom: "2px solid #ccc",
                      }}
                    >
                      TARİH
                    </th>

                    {machineKeys.map((key) => (
                      <th
                        key={key}
                        className="fw-bold bg-light"
                        style={{
                          minWidth: "100px",
                          borderLeft: "1px solid #aaa",
                          borderBottom: "2px solid #ccc",
                        }}
                      >
                        MAKİNA - {key}
                      </th>
                    ))}

                    <th
                      className="fw-bold bg-white text-dark"
                      style={{
                        minWidth: "100px",
                        borderLeft: "2px solid #000",
                        borderBottom: "2px solid #ccc",
                      }}
                    >
                      TOPLAM / GÜN
                    </th>
                    <th
                      className="fw-bold bg-white text-dark"
                      style={{
                        minWidth: "100px",
                        borderLeft: "2px solid #000",
                        borderBottom: "2px solid #ccc",
                      }}
                    >
                      TOPLAM / GÜN / 450
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {tableRows.map((row, idx) => {
                    const isExpanded = expandedDate === row.fullDate;

                    return (
                      <tr
                        key={idx}
                        onClick={() => toggleRow(row.fullDate)}
                        style={{
                          cursor: "pointer",
                          backgroundColor: isExpanded ? "#f0f8ff" : "white",
                        }}
                      >
                        {/* TARİH */}
                        <td className="fw-bold text-start ps-2">
                          <div className="d-flex align-items-center gap-1">
                            <i
                              className={`bi ${
                                isExpanded
                                  ? "bi-caret-down-fill"
                                  : "bi-caret-right-fill"
                              } text-muted`}
                              style={{ fontSize: "10px" }}
                            ></i>
                            {row.dateDisplay}
                          </div>
                        </td>

                        {/* MAKİNELER */}
                        {machineKeys.map((key) => {
                          const data = row.machines[key];
                          return (
                            <td
                              key={key}
                              style={{
                                borderLeft: "1px solid #aaa",
                                verticalAlign: "top",
                                padding: "4px",
                              }}
                            >
                              <div className="fw-bold text-dark">
                                {formatNum(data.toplam)}
                              </div>

                              {isExpanded && (
                                <div
                                  className="mt-1 pt-1 border-top fade-in"
                                  style={{ fontSize: "10px" }}
                                >
                                  <div className="d-flex justify-content-between text-success fw-bold">
                                    <span>Fiili Çalışma Süresi:</span>
                                    <span>{formatNum(data.fiili)}</span>
                                  </div>
                                  <div className="d-flex justify-content-between text-danger fw-bold">
                                    <span>Duruş Toplamı:</span>
                                    <span>{formatNum(data.durus)}</span>
                                  </div>
                                </div>
                              )}
                            </td>
                          );
                        })}

                        {/* TOPLAM / GÜN */}
                        <td
                          style={{
                            borderLeft: "2px solid #000",
                            backgroundColor: "#fff",
                            verticalAlign: "top",
                            padding: "4px",
                          }}
                        >
                          <div className="fw-bold text-dark">
                            {formatNum(row.dailyTotal.toplam)}
                          </div>

                          {isExpanded && (
                            <div
                              className="mt-1 pt-1 border-top border-dark fade-in"
                              style={{ fontSize: "10px" }}
                            >
                              <div className="d-flex justify-content-between text-success fw-bold">
                                <span>TF:</span>
                                <span>{formatNum(row.dailyTotal.fiili)}</span>
                              </div>
                              <div className="d-flex justify-content-between text-danger fw-bold">
                                <span>TD:</span>
                                <span>{formatNum(row.dailyTotal.durus)}</span>
                              </div>
                            </div>
                          )}
                        </td>

                        {/* TOPLAM / GÜN / 450 */}
                        <td
                          style={{
                            borderLeft: "1px solid #aaa",
                            backgroundColor: "#fff",
                            verticalAlign: "top",
                            padding: "4px",
                          }}
                        >
                          <div className="fw-bold text-dark">
                            {formatNum(
                              Math.round(
                                ((row.dailyTotal.toplam || 0) / 450) * 10
                              ) / 10
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {/* GENEL TOPLAM */}
                  <tr
                    className="fw-bold sticky-bottom bg-white"
                    style={{ bottom: 0, borderTop: "3px solid #000" }}
                  >
                    <td style={{ fontSize: "11px" }}>GENEL TOPLAM</td>

                    {machineKeys.map((key) => {
                      const monthlyData = apiData.makinaAylikToplamSure[
                        key
                      ] || {
                        toplam: 0,
                        fiili: 0,
                        durus: 0,
                      };

                      return (
                        <td
                          key={key}
                          style={{
                            borderLeft: "1px solid #aaa",
                            padding: "6px",
                          }}
                        >
                          <div>{formatNum(monthlyData.toplam)}</div>
                          <div
                            className="d-flex justify-content-center gap-2 mt-1"
                            style={{ fontSize: "10px", opacity: 0.8 }}
                          >
                            <span className="text-success">
                              {formatNum(monthlyData.fiili)}
                            </span>{" "}
                            |
                            <span className="text-danger">
                              {formatNum(monthlyData.durus)}
                            </span>
                          </div>
                        </td>
                      );
                    })}

                    {/* FABRİKA GENELİ */}
                    <td
                      style={{
                        borderLeft: "2px solid #000",
                        backgroundColor: "#fff",
                        padding: "6px",
                      }}
                    >
                      <div>{formatNum(grandTotals.toplam)}</div>
                      <div
                        className="d-flex justify-content-center gap-2 mt-1"
                        style={{ fontSize: "10px", opacity: 0.8 }}
                      >
                        <span className="text-success">
                          {formatNum(grandTotals.fiili)}
                        </span>{" "}
                        |
                        <span className="text-danger">
                          {formatNum(grandTotals.durus)}
                        </span>
                      </div>
                    </td>

                    {/* FABRİKA GENELİ / 450 */}
                    <td
                      style={{
                        borderLeft: "1px solid #aaa",
                        backgroundColor: "#fff",
                        padding: "6px",
                      }}
                    >
                      {formatNum(
                        Math.round(((grandTotals.toplam || 0) / 450) * 10) / 10
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>

            <Row className="mt-3">
              <Col md={12}>
                <Card className="shadow-sm border-0">
                  <Card.Header
                    className="bg-white fw-bold py-1"
                    style={{ fontSize: "13px" }}
                  >
                    Aylık Fiili Çalışma vs Duruş Analizi
                  </Card.Header>
                  <Card.Body className="p-2">
                    <div style={{ height: "250px" }}>
                      {chartData && (
                        <Bar
                          data={chartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: "top",
                                labels: { boxWidth: 10, font: { size: 11 } },
                              },
                              title: { display: false },
                            },
                            scales: {
                              x: {
                                stacked: true,
                                ticks: { font: { size: 11 } },
                              },
                              y: {
                                stacked: true,
                                beginAtZero: true,
                                ticks: { font: { size: 11 } },
                              },
                            },
                          }}
                        />
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>

      <style>{`
        .fade-in { animation: fadeIn 0.2s ease-in-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-2px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </Layout>
  );
}

export default BoruSureAnaliz;
